/**
 * TenantContext — admin tenant identity bootstrap (Sprint B — Master Plan v5.1).
 *
 * Mirrors storefront/contexts/TenantContext.tsx but uses admin-specific storage
 * registry. Admin shares the public `/api/public/tenant-context` endpoint to
 * avoid duplicating tenant resolution logic.
 *
 * Side effects on mount:
 *   1. Sets window.__TENANT_SLUG__ and window.__TENANT_ID__ so StorageSlot can
 *      read/write tenant-prefixed keys.
 *   2. Runs post-bootstrap migrations (un-prefixed → t:<slug>:).
 *   3. Validates admin auth_token against backend; migrates only if backend
 *      confirms current tenant matches token's tenant claim.
 *
 * Until tenant resolves, the rest of the app sees `null` from useTenant(),
 * and any premature StorageSlot.set() throws (rule #2 — never write t:unknown:*).
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import {
  runPostBootstrapMigrations,
  migrateAdminAuthTokenForCurrentTenant,
} from '../lib/storage'
import type { TenantBrand } from '@tenant/tenant-template-shared'

export type { TenantBrand } from '@tenant/tenant-template-shared'

export interface TenantData {
  id: string
  slug: string
  /** Folder + URL slug under /tenants/<assetSlug>/. Set by Sprint G endpoint. */
  assetSlug: string
  primaryDomain: string | null
  /** Brand identity block (Sprint G). Null until template is seeded. */
  brand: TenantBrand | null
  theme: {
    colors?: { primary?: string; secondary?: string; accent?: string }
    fonts?: { body?: string; heading?: string }
    logoUrl?: string | null
  } | null
}

declare global {
  interface Window {
    __TENANT_SLUG__?: string
    __TENANT_ID__?: string
    __TENANT_ASSET_SLUG__?: string
  }
}

/**
 * Validate that the input looks like a `#RRGGBB` hex color so we never
 * write garbage into a CSS color variable. Returns the normalized hex
 * (lowercase, with `#`) on success, empty string otherwise so callers
 * can `if (hex)`.
 *
 * Sprint H follow-up (2026-05-10): admin's generated `index.css` consumes
 * `--primary`/`--sidebar-primary` directly via `var(--primary)` (no
 * `hsl()` wrapper). Storefront uses `hsl(var(--primary))` and a different
 * applyTheme — do NOT cross the contracts. Writing an HSL triple here
 * produces `background-color: 117 14% 26%` which the browser drops as
 * invalid → invisible `bg-primary` buttons (BouquetsPage "Yeni buket",
 * empty-state "Buket yarat" etc).
 */
function normalizeHex(hex?: string | null): string {
  if (!hex) return ''
  const trimmed = hex.trim().replace(/^#/, '')
  if (!/^[0-9a-f]{6}$/i.test(trimmed)) return ''
  return `#${trimmed.toLowerCase()}`
}

const TenantCtx = createContext<TenantData | null>(null)

const API_BASE =
  (import.meta as { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? ''

interface TenantProviderProps {
  children: ReactNode
  fallback?: TenantData
}

export function TenantProvider({ children, fallback }: TenantProviderProps) {
  const [tenant, setTenant] = useState<TenantData | null>(fallback ?? null)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    // VITE_API_URL already ends with "/api", append only "/public/tenant-context".
    const base = API_BASE.replace(/\/+$/, '')
    const url = base
      ? `${base}/public/tenant-context`
      : '/api/public/tenant-context'

    // Bootstrap call — runs before apiClient has a token bound. Sprint H
    // Phase 4 ESLint rule whitelists this file via .eslintrc.json overrides.
    //
    // X-Forwarded-Host: admin and backend live on different hosts in prod
    // (e.g. admin.cicekci.az vs *.onrender.com). Browser cannot set Host
    // header, so backend.resolveTenant cannot find TenantDomain row by request
    // Host. Forward the admin's host explicitly. corsConfig allowlist
    // includes X-Forwarded-Host (see backend/middleware/corsConfig.ts).
    const tenantHeaders: Record<string, string> = {}
    if (typeof window !== 'undefined' && window.location?.host) {
      tenantHeaders['X-Forwarded-Host'] = window.location.host
    }
    fetch(url, { credentials: 'include', headers: tenantHeaders })
      .then(r =>
        r.ok ? r.json() : Promise.reject(new Error(`tenant-context HTTP ${r.status}`)),
      )
      .then(async (data: TenantData) => {
        // Set slug FIRST — storage migrations + StorageSlot tenant prefix
        // need it before any other module reads/writes storage.
        if (typeof window !== 'undefined') {
          window.__TENANT_SLUG__ = data.slug
          window.__TENANT_ID__ = data.id
          window.__TENANT_ASSET_SLUG__ = data.assetSlug
          // Sprint G Phase 6: project minimal brand tokens onto :root so
          // tenant-aware inline styles (LoginForm CSS-var fallbacks) and
          // Tailwind `bg-primary` resolve to the right colour. Storefront has
          // a richer applyTheme; admin only needs primary + primary-foreground
          // for now.
          const c = data.theme?.colors
          if (c) {
            const root = document.documentElement
            const primary = c.primary
            if (primary) {
              root.style.setProperty('--brand-primary-base', primary)
              // Admin's generated CSS uses `var(--primary)` directly (no
              // `hsl()` wrapper) — see index.css. Write the hex as-is so
              // `bg-primary` / `text-primary` resolve to a real color
              // instead of an invalid CSS value. Match for --sidebar-primary,
              // which shadcn's Sidebar reads the same way.
              const primaryHex = normalizeHex(primary)
              if (primaryHex) {
                root.style.setProperty('--primary', primaryHex)
                root.style.setProperty('--sidebar-primary', primaryHex)
              }
              // Sprint H (2026-05-10): drive `<meta theme-color>` from the
              // tenant primary so the mobile browser chrome matches the
              // brand. index.html ships a neutral white default so the
              // pre-mount flash is white-on-white instead of a generic
              // teal. Admin doesn't have a tenant-paint Vite plugin yet,
              // so this still has a brief flash (white -> brand) on cold
              // load — acceptable because admin is overwhelmingly used
              // by logged-in returning sessions where the brand has
              // already been written to the document.
              const themeMeta = document.querySelector<HTMLMetaElement>(
                'meta[name="theme-color"]',
              )
              if (themeMeta) themeMeta.setAttribute('content', primary)
            }
          }
        }

        // Sprint B.3: migrate un-prefixed keys → t:<slug>:<key>.
        runPostBootstrapMigrations()

        // Sprint B.4: validate auth_token against backend; migrate only if
        // backend confirms the token belongs to current tenant.
        void migrateAdminAuthTokenForCurrentTenant(API_BASE)

        setTenant(data)
        setLoaded(true)
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : String(err))
        setLoaded(true)
      })
  }, [])

  if (error && !tenant) {
    return <TenantNotConfigured />
  }

  return (
    <TenantCtx.Provider value={loaded ? tenant : fallback ?? null}>
      {children}
    </TenantCtx.Provider>
  )
}

/**
 * Rendered when tenant resolution fails (typically because the dev opened the
 * admin on `localhost:5173` instead of the tenant-bound host like
 * `tenant-a.local:5173`). Backend's `resolveTenant` middleware only knows
 * registered hosts — `localhost` deliberately does not resolve to any tenant
 * (rule #1 of feedback_tenant_enforcement_rules.md: never fallback to
 * t:unknown:*).
 *
 * We do NOT add a frontend bypass: backend remains the single source of
 * tenant identity. We only improve the UX so the dev sees actionable
 * instructions instead of a blank screen.
 */
function TenantNotConfigured(): JSX.Element {
  const host =
    typeof window !== 'undefined' ? window.location.host : 'this host'
  const isLocalDev = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(host)
  // Pilot tenant hosts mirror seed-pilot-local.ts; keep them in sync if the
  // pilot seed adds new tenants. These are dev-only hints — production tenants
  // resolve via TenantDomain rows seeded by the operator.
  const pilotHosts = ['tenant-a.local:5173', 'tenant-b.local:5173']

  return (
    <div
      style={{
        padding: 32,
        fontFamily: 'system-ui',
        color: '#1f2937',
        maxWidth: 640,
        margin: '64px auto',
        lineHeight: 1.5,
      }}
    >
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 12px' }}>
        Tenant not configured for {host}
      </h1>
      {isLocalDev ? (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Local dev tip: open the admin via a tenant host so the backend can
            resolve your pilot tenant from the <code>Host</code> header. Make
            sure your hosts file maps both pilot tenants to{' '}
            <code>127.0.0.1</code>:
          </p>
          <pre
            style={{
              background: '#f3f4f6',
              padding: 12,
              borderRadius: 6,
              fontSize: 13,
              margin: '0 0 12px',
            }}
          >
            127.0.0.1 tenant-a.local tenant-b.local
          </pre>
          <p style={{ margin: '0 0 8px' }}>Then open one of:</p>
          <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
            {pilotHosts.map((h) => (
              <li key={h}>
                <a href={`http://${h}`} style={{ color: '#0d9488' }}>
                  http://{h}
                </a>
              </li>
            ))}
          </ul>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
            See <code>docs/PILOT_LOCAL_VERIFY.md</code> for the full pilot
            bring-up checklist.
          </p>
        </>
      ) : (
        <p style={{ margin: 0, color: '#6b7280' }}>
          No tenant is bound to this hostname. Ask an operator to add a
          TenantDomain row that maps <code>{host}</code> to your tenant.
        </p>
      )}
    </div>
  )
}

export function useTenant(): TenantData | null {
  return useContext(TenantCtx)
}

export function useRequiredTenant(): TenantData {
  const ctx = useContext(TenantCtx)
  if (!ctx) {
    throw new Error(
      'useRequiredTenant: tenant has not loaded yet (or used outside TenantProvider)',
    )
  }
  return ctx
}
